import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import Dropzone from "../components/Dropzone";
import { Scissors, DownloadSimple, CircleNotch } from "@phosphor-icons/react";

export default function Split() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState(2);
  const [cols, setCols] = useState(2);
  const [tiles, setTiles] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!file) return setPreview(null);
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const run = async () => {
    if (!file) return;
    setLoading(true);
    setTiles(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("rows", String(rows));
      fd.append("cols", String(cols));
      const { data } = await api.post("/split-decal", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTiles(data.tiles);
      toast.success(`Split into ${data.tiles.length} tiles`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadAll = () => {
    if (!tiles) return;
    tiles.forEach((t, idx) => {
      const a = document.createElement("a");
      a.href = `data:image/png;base64,${t.png_base64}`;
      a.download = `tile-r${t.row}-c${t.col}.png`;
      setTimeout(() => a.click(), idx * 150);
    });
  };

  return (
    <div className="max-w-[1440px] mx-auto">
      <div className="px-6 md:px-10 py-6 border-b border-[var(--border)] flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="micro-label">/ Decal splitter</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">
            Big logo, many tiles.
          </h1>
          <p className="text-[var(--fg-2)] text-sm mt-2 max-w-xl">
            Split oversized artwork into a grid of PNGs. Convert each one and
            reassemble in the livery editor.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={run} disabled={!file || loading} className="btn-primary" data-testid="btn-split-run">
            {loading ? <><CircleNotch className="animate-spin" size={14} weight="bold"/> Slicing</> : <><Scissors size={14} weight="bold"/> Split</>}
          </button>
          <button onClick={downloadAll} disabled={!tiles} className="btn-accent" data-testid="btn-download-tiles">
            <DownloadSimple size={14} weight="bold" /> Download All
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] border-b border-[var(--border)]">
        <aside className="bg-white border-r border-[var(--border)]">
          <div className="p-5 border-b border-[var(--border)]">
            <div className="micro-label mb-3">Grid</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-[var(--fg-2)] mb-1">Rows</div>
                <input
                  type="number"
                  min={1}
                  max={6}
                  value={rows}
                  onChange={(e) => setRows(Math.max(1, Math.min(6, parseInt(e.target.value || "1"))))}
                  className="h-10 w-full border border-[var(--border)] px-3 font-mono"
                  data-testid="input-rows"
                />
              </div>
              <div>
                <div className="text-xs text-[var(--fg-2)] mb-1">Cols</div>
                <input
                  type="number"
                  min={1}
                  max={6}
                  value={cols}
                  onChange={(e) => setCols(Math.max(1, Math.min(6, parseInt(e.target.value || "1"))))}
                  className="h-10 w-full border border-[var(--border)] px-3 font-mono"
                  data-testid="input-cols"
                />
              </div>
            </div>
            <div className="mt-3 font-mono text-xs text-[var(--fg-2)]">
              = {rows * cols} tiles
            </div>
          </div>
          {preview && (
            <div className="p-5 border-b border-[var(--border)]">
              <div className="micro-label mb-2">Preview grid</div>
              <div
                className="relative checker border border-[var(--border)]"
                style={{ aspectRatio: "1/1" }}
              >
                <img src={preview} alt="preview" className="absolute inset-0 w-full h-full object-contain" />
                <div
                  className="absolute inset-0 grid"
                  style={{
                    gridTemplateColumns: `repeat(${cols}, 1fr)`,
                    gridTemplateRows: `repeat(${rows}, 1fr)`,
                  }}
                >
                  {Array(rows * cols).fill(0).map((_, i) => (
                    <div key={i} className="border border-[var(--accent)] border-dashed"></div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </aside>

        <main className="bg-[var(--bg-2)] p-6 md:p-10">
          {!file ? (
            <Dropzone onFile={setFile} />
          ) : !tiles ? (
            <div className="hairline bg-white p-10 flex flex-col items-center text-center">
              <div className="font-display font-bold text-xl">{file.name}</div>
              <div className="text-sm text-[var(--fg-2)] mt-1">
                {(file.size / 1024).toFixed(1)} KB
              </div>
              <div className="mt-6 text-[var(--fg-2)] text-sm">
                Adjust the grid and press Split.
              </div>
            </div>
          ) : (
            <div
              className="grid gap-px bg-[var(--border)] border border-[var(--border)]"
              style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
              data-testid="tiles-grid"
            >
              {tiles.map((t) => (
                <div key={`${t.row}-${t.col}`} className="bg-white">
                  <div className="checker aspect-square flex items-center justify-center">
                    <img
                      src={`data:image/png;base64,${t.png_base64}`}
                      alt={`tile ${t.row}-${t.col}`}
                      className="max-h-full max-w-full"
                    />
                  </div>
                  <div className="p-3 border-t border-[var(--border)] flex items-center justify-between text-xs font-mono">
                    <span>R{t.row} · C{t.col}</span>
                    <a
                      href={`data:image/png;base64,${t.png_base64}`}
                      download={`tile-r${t.row}-c${t.col}.png`}
                      className="underline hover:text-[var(--accent)]"
                    >
                      Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
